/**
 * Statistics Types
 *
 * Type definitions for Canadian financial statistics used in help text
 * and age-appropriate field defaults.
 *
 * Per-age statistics are served as static JSON files from
 * public/data/statistics/age-{age}.json and fetched on demand.
 */

// ---------------------------------------------------------------------------
// Per-age statistics (used by runtime code)
// ---------------------------------------------------------------------------

/**
 * A single field's statistic for one specific age.
 * This is the shape stored in each per-age JSON file.
 */
export interface AgeFieldStatistic {
  average: number;
  unit: "currency" | "percentage" | "years" | "count";
  category: string;
  displayName: string;
  source: string;
}

/**
 * Map of fieldName → statistic, for a single age.
 * Loaded from one per-age JSON file and cached in session storage.
 */
export type AgeStatistics = Record<string, AgeFieldStatistic>;

/**
 * Shape of each per-age JSON file under public/data/statistics/.
 */
export interface AgeStatisticsFile {
  age: number;
  generatedAt: string;
  fields: AgeStatistics;
}

/**
 * Format a per-age field statistic for display in help text.
 *
 * Currency values are rounded to the nearest $100 with an "approx." prefix
 * (see TODO #24 acceptance criteria).
 */
export function formatStatisticHelpText(stat: AgeFieldStatistic): string {
  const { average, unit } = stat;
  switch (unit) {
    case "currency": {
      const rounded = Math.round(average / 100) * 100;
      return `(Age average: ~$${rounded.toLocaleString()})`;
    }
    case "percentage":
      return `(Age average: ${(average * 100).toFixed(1)}%)`;
    case "years":
      return `(Age average: ${Math.round(average)} years)`;
    case "count":
      return `(Age average: ${Math.round(average)})`;
    default:
      return `(Average: ${average})`;
  }
}

// ---------------------------------------------------------------------------
// Full-dataset types (used only by generation scripts, NOT runtime code)
// ---------------------------------------------------------------------------

/**
 * Age-specific statistic for a single field
 */
export interface AgeStatistic {
  age: number; // Age in years (18-100)
  average: number; // Average value for Canadians at this age
  median?: number; // Optional median value (50th percentile)
  percentile25?: number; // Optional 25th percentile
  percentile75?: number; // Optional 75th percentile
  percentage?: number; // Optional percentage (e.g., % who own homes)
  description?: string; // Optional context-specific description
}

/**
 * Complete statistics for a single field across all ages
 */
export interface FieldStatistics {
  fieldName: string; // Matches the field name in settings types
  displayName: string; // Human-readable name for the field
  category:
    | "profile"
    | "investableAssets"
    | "realEstateAssets"
    | "liabilities"
    | "income"
    | "expenses"
    | "assumptions";
  unit: "currency" | "percentage" | "years" | "count"; // Type of value
  byAge: AgeStatistic[]; // Array of statistics, one per age (18-100)
  source: string; // Data source citation
  lastUpdated: string; // ISO date string of last update
  notes?: string; // Additional context or methodology notes
}

/**
 * All statistics organized by field name
 */
export type StatisticsDatabase = Record<string, FieldStatistics>;

/**
 * Firestore document structure for a field's statistics
 */
export interface StatisticsDocument {
  fieldName: string;
  displayName: string;
  category: string;
  unit: string;
  byAge: AgeStatistic[];
  source: string;
  lastUpdated: string;
  notes?: string;
}

/**
 * Helper to get statistic for a specific age.
 *
 * @internal Used only by generation scripts — not intended for runtime code.
 */
export function getStatisticForAge(
  statistics: FieldStatistics,
  age: number | null
): AgeStatistic | null {
  if (age === null || age < 18) return null;

  // For ages over 100, use 100's data
  const lookupAge = Math.min(age, 100);

  // Find exact match
  const stat = statistics.byAge.find((s) => s.age === lookupAge);
  if (stat) return stat;

  // If not found, interpolate between nearest ages
  const below = statistics.byAge
    .filter((s) => s.age < lookupAge)
    .sort((a, b) => b.age - a.age)[0];
  const above = statistics.byAge
    .filter((s) => s.age > lookupAge)
    .sort((a, b) => a.age - b.age)[0];

  if (!below && !above) return null;
  if (!below) return above;
  if (!above) return below;

  // Linear interpolation
  const ratio = (lookupAge - below.age) / (above.age - below.age);
  return {
    age: lookupAge,
    average: below.average + ratio * (above.average - below.average),
    median:
      below.median && above.median
        ? below.median + ratio * (above.median - below.median)
        : undefined,
    percentile25:
      below.percentile25 && above.percentile25
        ? below.percentile25 + ratio * (above.percentile25 - below.percentile25)
        : undefined,
    percentile75:
      below.percentile75 && above.percentile75
        ? below.percentile75 + ratio * (above.percentile75 - below.percentile75)
        : undefined,
    percentage:
      below.percentage && above.percentage
        ? below.percentage + ratio * (above.percentage - below.percentage)
        : undefined,
  };
}

/**
 * Format a full-dataset statistic for display.
 *
 * Unlike {@link formatStatisticHelpText} which operates on the per-age
 * runtime type (`AgeFieldStatistic`), this function operates on
 * `AgeStatistic` from the full statistics database and does not round
 * currency values.
 *
 * @internal Used only by generation scripts — not intended for runtime code.
 */
export function formatGenerationStatistic(
  statistic: AgeStatistic | null,
  unit: "currency" | "percentage" | "years" | "count"
): string {
  if (!statistic) return "";

  switch (unit) {
    case "currency":
      return `(Age average: $${Math.round(statistic.average).toLocaleString()})`;
    case "percentage":
      return `(Age average: ${(statistic.average * 100).toFixed(1)}%)`;
    case "years":
      return `(Age average: ${Math.round(statistic.average)} years)`;
    case "count":
      return `(Age average: ${Math.round(statistic.average)})`;
    default:
      return `(Average: ${statistic.average})`;
  }
}
